import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Award, Shield, CheckCircle } from "lucide-react";

const About = () => {
  return (
    <section id="about" className="py-8 md:py-16 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-6 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-dark mb-2 md:mb-4">About NearLab Services</h2>
          <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
            We bring AABB-certified testing services directly to you, ensuring accuracy, reliability, and your privacy.
          </p>
        </div>

        {/* AABB Certification Highlight */}
        <div className="mb-6 md:mb-8">
          <Card className="border-2 border-primary-dark bg-gradient-to-r from-primary-light/50 to-blue-light/30">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary-dark rounded-full">
                    <Award className="text-white" size={24} />
                  </div>
                  <Badge variant="outline" className="bg-white text-primary-dark font-semibold px-3 py-1">
                    AABB CERTIFIED
                  </Badge>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-primary-dark mb-2">Laboratory Accreditation You Can Trust</h3>
                  <p className="text-sm md:text-base text-gray-700">
                    Our partner laboratories are accredited by the American Association of Blood Banks (AABB), 
                    the gold standard for relationship testing. This ensures maximum accuracy, reliability, and 
                    legal admissibility of all test results.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-8 items-center mb-6 md:mb-8">
          {/* Family Picture Card */}
          <Card className="overflow-hidden">
            <div className="w-full">
              <AspectRatio ratio={16/9} className="bg-muted">
                <img
                  src="/lovable-uploads/01c5c37c-6644-4755-b63a-8a4179a73d1a.png"
                  alt="Family walking together - Mobile DNA testing services"
                  className="w-full h-full object-cover rounded-t-lg"
                  loading="lazy"
                />
              </AspectRatio>
            </div>
            <CardContent className="pt-3 md:pt-4 pb-3 md:pb-6">
              <p className="text-xs md:text-sm text-gray-500 italic text-center">
                Serving families throughout Houston with convenient mobile testing.
              </p>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-primary-dark">
            <CardHeader className="pb-3 md:pb-6">
              <CardTitle className="text-lg md:text-xl text-primary-dark">How We Work</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm md:text-base text-gray-700">
                We keep testing simple. Our certified technicians come to your home, office, or any location you choose. We collect samples properly and follow all steps to make sure results can be used in court if needed.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 md:mt-12">
          <Card className="border-l-4 border-primary-dark">
            <CardHeader className="pb-3 md:pb-6">
              <CardTitle className="text-lg md:text-xl text-primary-dark flex items-center gap-2">
                <Shield size={20} />
                Quality Assurance & Certification
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                Our commitment to accuracy and reliability is backed by industry-leading certifications:
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-primary-dark mt-0.5 md:mt-1 flex-shrink-0" />
                  <span className="text-sm md:text-base">AABB-accredited laboratory partners</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-primary-dark mt-0.5 md:mt-1 flex-shrink-0" />
                  <span className="text-sm md:text-base">Certified sample collection technicians</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-primary-dark mt-0.5 md:mt-1 flex-shrink-0" />
                  <span className="text-sm md:text-base">Strict chain of custody protocols</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-primary-dark mt-0.5 md:mt-1 flex-shrink-0" />
                  <span className="text-sm md:text-base">Court-admissible results</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-primary-dark mt-0.5 md:mt-1 flex-shrink-0" />
                  <span className="text-sm md:text-base">99.9%+ accuracy guarantee</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 text-primary-dark mt-0.5 md:mt-1 flex-shrink-0" />
                  <span className="text-sm md:text-base">ISO-compliant testing standards</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default About;
